import React from "react";
import { CalendarDays, Target, Hash } from "lucide-react";
import { fetchCommonNumber } from "../lib/api";
import { useCachedData } from "../lib/useCachedData";

function formatDMY(iso) {
  if (!iso) return "--/--/----";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function CommonNumbersSection({ variant, title = "Daily Common Number" }) {
  const { data } = useCachedData(`mst:common:${variant}`, () => fetchCommonNumber(variant), {
    pollMs: 300000,
  });

  return (
    <div
      data-testid="common-numbers-section"
      className="bg-white rounded-2xl border border-slate-200 card-shadow overflow-hidden max-w-2xl mx-auto"
    >
      <div className="bg-gradient-to-r from-[#ff4500] to-[#f97316] px-5 py-3">
        <h2 className="text-white font-head font-bold text-center text-base sm:text-lg tracking-wide">
          {title.toUpperCase()}
        </h2>
      </div>
      <div className="px-5 sm:px-8 py-6">
        <div className="flex items-center justify-center gap-2 text-slate-700 font-semibold mb-6">
          <CalendarDays size={18} className="text-[#ff4500]" />
          <span data-testid="common-date">Date: {formatDMY(data?.date)}</span>
        </div>

        <div className="flex flex-col items-center">
          <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
            <Target size={16} className="text-[#ff4500]" /> Direct Common Number
          </p>
          <span
            data-testid="common-direct"
            className="inline-flex items-center justify-center w-28 h-28 rounded-full border-4 border-[#ff4500]/60 bg-orange-50 font-num text-6xl font-extrabold text-slate-900"
          >
            {data?.direct ?? "--"}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="rounded-xl bg-slate-50 border border-slate-200 py-4 text-center">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">House</p>
            <p data-testid="common-house" className="font-num text-3xl font-extrabold text-[#ff4500] mt-1">
              {data?.house ?? "-"}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 py-4 text-center">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Ending</p>
            <p data-testid="common-ending" className="font-num text-3xl font-extrabold text-slate-900 mt-1">
              {data?.ending ?? "-"}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1">
            <Hash size={16} className="text-[#ff4500]" /> Today's Common Numbers
          </p>
          <div className="flex flex-wrap gap-2" data-testid="common-list">
            {(data?.common_numbers || []).map((n, i) => (
              <span
                key={i}
                className="font-num font-bold text-slate-900 bg-orange-100 border border-orange-200 rounded-lg px-3 py-2 text-lg"
              >
                {n}
              </span>
            ))}
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-400 text-center leading-relaxed">
          Common numbers are informational references derived from a fixed date-based method and do
          not guarantee any outcome. Numbers roll over automatically after 8:00 PM (IST).
        </p>
      </div>
    </div>
  );
}
